/**
 * useIsIpad / useIsTouch — runtime device hints used to bypass UI
 * patterns that misbehave on touch hardware.
 *
 * `useIsTouch` returns true when the primary input is touch (iPad,
 * iPhone, Android). We use it to skip Radix tooltip wrappers in the
 * sidebar — on iPad Safari those tooltips capture the first tap so
 * nav rows require two taps to navigate.
 */
import { useEffect, useState } from 'react';

function detectTouch(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(pointer: coarse)').matches) return true;
  } catch {
    /* ignore */
  }
  return (
    'ontouchstart' in window ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
  );
}

function detectIpad(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad/i.test(ua)) return true;
  // iPadOS 13+ reports as Mac — disambiguate via touch points.
  return /Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
}

export function useIsTouch(): boolean {
  const [isTouch, setIsTouch] = useState<boolean>(() => detectTouch());
  useEffect(() => {
    setIsTouch(detectTouch());
    const mq = window.matchMedia?.('(pointer: coarse)');
    if (!mq) return;
    const handler = () => setIsTouch(detectTouch());
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return isTouch;
}

export function useIsIpad(): boolean {
  const [isIpad, setIsIpad] = useState<boolean>(() => detectIpad());
  useEffect(() => {
    setIsIpad(detectIpad());
  }, []);
  return isIpad;
}

export default useIsIpad;